import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { getListVaultsQueryKey, getUpdateVaultMutationOptions, updateVault, type VaultInput } from '@/modules/api/codegen';
import { actions as messagesActions } from '@/modules/messages';
import { isBadRequestError } from '@/services/utilities/isApiError';

export interface UpdateVaultVariables {
    vaultId: string;
    /**
     * `VaultInput` rather than the endpoint's all-optional body: a member array replaces the whole list
     * and an omitted field keeps its current value, so the name and both lists always travel together.
     */
    input: VaultInput;
}

// Mutations bypass the shared query cache's `onError`, so this reports both outcomes through the
// messages module itself, as in `useCreateVault`.
export const useUpdateVault = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: getUpdateVaultMutationOptions().mutationKey,
        mutationFn: ({ vaultId, input }: UpdateVaultVariables) => updateVault({ vaultId }, input),
        onSuccess: async (_, { input }) => {
            // The sidebar and the settings table read the same list, so one invalidation refreshes both —
            // including a renamed vault's sidebar entry.
            await queryClient.invalidateQueries({ queryKey: getListVaultsQueryKey() });

            dispatch(
                messagesActions.displaySuccessMessage({
                    message: { id: 'vaults.form.edit.success', values: { vault: input.name } },
                    options: { duration: 5 },
                }),
            );
        },
        onError: error =>
            dispatch(
                messagesActions.displayErrorMessage({
                    // The form blocks a save that leaves no editor, but an editor can be group-derived, so
                    // the client's view may not match the server's. Removing the last editor is the only
                    // bad request this endpoint documents.
                    message: { id: isBadRequestError(error) ? 'vaults.form.error.lastEditor' : 'vaults.form.edit.error' },
                    options: { duration: 5 },
                }),
            ),
    });
};
